/** @jsxImportSource theme-ui */
import React, { useMemo, useState, useCallback } from 'react';
import {
  Flex,
  Text,
  Pill,
  Button,
  TextArea,
  Avatar,
  Divider,
  Modal,
} from '@infotrack/zenith-ui';
import type {
  ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdTopFivePermissionsDto,
} from '@/api/models';
import type { TopFiveItemType } from '@/api/types/topFiveItemType';
import { useTopFiveComments } from '../hooks/useTopFiveComments';
import {
  containerStyles,
  headerStyles,
  formStyles,
  commentsListStyles,
  commentItemStyles,
  commentHeaderStyles,
  userInfoStyles,
  actionsStyles,
} from './topFiveComments.styles';

type TopFiveCommentsProps = {
  item: TopFiveItemType;
  permissions?: ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdTopFivePermissionsDto;
  topFiveId?: number;
};

export const TopFiveComments = React.memo(
  ({ item, permissions, topFiveId }: TopFiveCommentsProps) => {
    const [newCommentText, setNewCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState<number | null>(
      null
    );
    const [editingText, setEditingText] = useState('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<number | null>(null);

    const {
      addComment,
      updateComment,
      deleteComment,
      isAdding,
      isUpdating,
      isDeleting,
    } = useTopFiveComments(item.topFiveItemId, topFiveId);

    // Comment list from item - sorted by most recent first
    const comments = useMemo(() => {
      const allComments = ('comments' in item && item.comments) || [];
      return [...allComments].sort((a, b) => {
        const dateA = new Date(a.createdOn || 0).getTime();
        const dateB = new Date(b.createdOn || 0).getTime();
        return dateB - dateA;
      });
    }, [item]);

    const commentCount = comments.length;

    // Add comment handler
    const handleAddComment = useCallback(async () => {
      const success = await addComment(newCommentText);
      if (success) {
        setNewCommentText('');
      }
    }, [addComment, newCommentText]);

    // Start editing
    const handleStartEdit = useCallback(
      (commentId: number, currentText: string) => {
        setEditingCommentId(commentId);
        setEditingText(currentText);
      },
      []
    );

    // Save edit
    const handleSaveEdit = useCallback(async () => {
      if (editingCommentId) {
        const success = await updateComment(editingCommentId, editingText);
        if (success) {
          setEditingCommentId(null);
          setEditingText('');
        }
      }
    }, [editingCommentId, editingText, updateComment]);

    // Cancel editing
    const handleCancelEdit = useCallback(() => {
      setEditingCommentId(null);
      setEditingText('');
    }, []);

    // Format date
    const formatDate = useCallback((dateString: string | null | undefined) => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString(navigator.language);
    }, []);

    // Decode HTML entities
    const decodeHtmlEntities = useCallback((text: string): string => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }, []);

    // Delete modal handlers
    const handleOpenDeleteModal = useCallback((commentId: number) => {
      setCommentToDelete(commentId);
      setDeleteModalOpen(true);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
      setDeleteModalOpen(false);
      setCommentToDelete(null);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (commentToDelete) {
        const success = await deleteComment(commentToDelete);
        if (success) {
          handleCloseDeleteModal();
        }
      }
    }, [commentToDelete, deleteComment, handleCloseDeleteModal]);

    return (
      <Flex sx={containerStyles}>
        {/* Header */}
        <Flex sx={headerStyles}>
          <Text variant="subtitle">Comments</Text>
          <Pill variant="static">{String(commentCount)}</Pill>
        </Flex>

        {/* Add Comment Form */}
        <Flex sx={formStyles}>
          <TextArea
            fullWidth={true}
            value={newCommentText}
            onChange={e => setNewCommentText(e.target.value)}
            placeholder="Update the progress of this top 5"
          />
          <Flex sx={{ gap: ['0.375rem', '0.5rem'] }}>
            <Button
              variant="primary"
              size="small"
              onClick={handleAddComment}
              disabled={!newCommentText.trim()}
              loading={isAdding}
            >
              Add comment
            </Button>
            {newCommentText.trim() && (
              <Button
                variant="ghost"
                size="small"
                onClick={() => setNewCommentText('')}
              >
                Cancel
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Divider */}
        {comments.length > 0 && (
          <Divider direction="horizontal" padding="0" color="neutral/200" />
        )}

        {/* Comments List */}
        <Flex sx={commentsListStyles}>
          {comments.length > 0 ? (
            comments.map((comment, index) => {
              const isEditing =
                editingCommentId === comment.topFiveItemCommentId;
              const isOwnComment =
                permissions?.username &&
                comment.staff?.username &&
                permissions.username === comment.staff.username;

              return (
                <React.Fragment key={comment.topFiveItemCommentId}>
                  <Flex sx={commentItemStyles}>
                    {/* Comment Header */}
                    <Flex sx={commentHeaderStyles}>
                      <Avatar
                        name={
                          comment.staff?.fullName ||
                          comment.staff?.firstName ||
                          'User'
                        }
                      />
                      <Flex sx={userInfoStyles}>
                        <Text variant="surtitle">
                          {comment.staff?.fullName ||
                            `${comment.staff?.firstName || ''} ${comment.staff?.surname || ''}`.trim() ||
                            'Unknown User'}
                        </Text>
                        <Text variant="small" color="neutral/700">
                          Created {formatDate(comment.createdOn)}
                        </Text>
                        {comment.updatedOn &&
                          comment.updatedOn !== comment.createdOn && (
                            <Text variant="small" color="neutral/700">
                              • Updated {formatDate(comment.updatedOn)}
                            </Text>
                          )}
                      </Flex>

                      {/* Edit/Delete Actions - only for own comments */}
                      {isOwnComment && !isEditing && (
                        <Flex sx={actionsStyles}>
                          <Button
                            variant="borderless"
                            size="small"
                            iconProps={{ name: 'edit', variant: 'outlined' }}
                            onClick={() =>
                              handleStartEdit(
                                comment.topFiveItemCommentId!,
                                comment.comments || ''
                              )
                            }
                            aria-label="Edit comment"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="borderless"
                            size="small"
                            iconProps={{ name: 'delete', variant: 'outlined' }}
                            onClick={() =>
                              handleOpenDeleteModal(
                                comment.topFiveItemCommentId!
                              )
                            }
                            aria-label="Delete comment"
                          >
                            Delete
                          </Button>
                        </Flex>
                      )}
                    </Flex>

                    {/* Comment Body or Edit Form */}
                    {isEditing ? (
                      <Flex sx={formStyles}>
                        <TextArea
                          fullWidth={true}
                          value={editingText}
                          onChange={e => setEditingText(e.target.value)}
                          placeholder="Edit comment"
                        />
                        <Flex sx={{ gap: ['0.375rem', '0.5rem'] }}>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={handleSaveEdit}
                            disabled={!editingText.trim()}
                            loading={isUpdating}
                          >
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </Flex>
                      </Flex>
                    ) : (
                      <Text variant="medium">
                        {decodeHtmlEntities(comment.comments || '')}
                      </Text>
                    )}
                  </Flex>

                  {/* Divider between comments */}
                  {index < comments.length - 1 && (
                    <Divider
                      direction="horizontal"
                      padding="0"
                      color="neutral/200"
                    />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <Text variant="medium" color="neutral/700">
              No comments yet. Be the first to add one!
            </Text>
          )}
        </Flex>

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          title="Delete Comment"
          size="small"
          useTitlePadding={true}
          primaryButtonProps={{
            variant: 'destructive',
            children: 'Delete',
            onClick: () => {
              handleConfirmDelete();
            },
            loading: isDeleting,
          }}
          secondaryButtonProps={{
            children: 'Cancel',
            onClick: handleCloseDeleteModal,
          }}
        >
          <Text variant="medium">
            Are you sure you want to delete this comment? This action cannot be
            undone.
          </Text>
        </Modal>
      </Flex>
    );
  }
);

TopFiveComments.displayName = 'TopFiveComments';
